/**
 * @file THE MIX — content, authored, hand-tuned. The other half of the sound
 * table; `sfxAssets.js` next door is GENERATED and says what exists, this file
 * says how loud it is, how far it carries and what may steal it.
 *
 * ⚠ THIS IS `src/game/`, SO THE ENGINE NEVER IMPORTS IT. `PlayRoute.jsx` builds
 * it and hands it down, exactly like `createFactionRules()`, `createWeaponTable()`
 * and `createDialogueScript()` — and for the same reason. With no table injected
 * the audio system constructs nothing, subscribes to nothing and draws no dice,
 * which is what keeps hero mode's golden checksum **1878938342** safe BY
 * CONSTRUCTION rather than by a test that has to be re-run.
 *
 * ⚠⚠ **EVERY NUMBER IN HERE IS UNPLAYTESTED, EXCEPT THE THREE THAT HAVE BEEN
 * REJECTED** — otherwise the `bulletToCar` 0.35 status. They are derived where a
 * derivation exists (see the notes on `maxDist` and `maxVoices`) and taste where
 * none does. Judge them on **`/play/sfx`**, which is the mandatory review tool
 * for this system, and re-tune HERE — never in the engine, and never by
 * re-normalising the clips, which would silently move every number on this page
 * at once.
 *
 * ⚠ **THE THREE PLAYTESTED ROWS ARE `car_engine.gain` (0.35 → 0.13),
 * `gunshot.maxDist` (110 → 80) and `siren_police.maxDist` (160 → 120), AND ALL
 * THREE OVERTURNED AN ARGUMENT THAT IS STILL WORTH READING.** Each row records
 * what was believed, what the owner reported and what replaced it, because the
 * belief is what a future reader will otherwise re-derive from scratch and
 * "fix" back. A playtest result outranks the arithmetic on this page.
 *
 * ## The fields
 *
 * | field | meaning |
 * |---|---|
 * | `bus` | `'sfx'` positional world sound · `'ambient'` 2D bed · `'ui'` 2D, unducked, plays while the sim is frozen |
 * | `gain` | mix level. The clips are peak-normalised to -1.5 dBFS, so this is a MIX decision and nothing else |
 * | `pick` | `'random'` a fresh variant every time · `'sticky'` one variant per entity key, for ever |
 * | `priority` | who survives when the voice pool is full. Higher wins |
 * | `maxVoices` | per-slug concurrency cap, applied BEFORE the pool |
 * | `minGap` | seconds between two starts of this slug, whoever asked |
 * | `refDist` | metres inside which the sound is at full `gain` |
 * | `maxDist` | metres past which it is never allocated at all |
 * | `rateVar` | ± playback-rate jitter per play, for variety |
 * | `rateKeyed` | ± playback-rate offset hashed from the entity key — STICKY pitch |
 * | `from` | alias: play another slug's buffer under this slug's tuning |
 *
 * ## Two rules that are not obvious
 *
 * ⚠ **`maxDist` IS A CULL, NOT A FADE, AND IT IS THE WHOLE PERFORMANCE STORY.**
 * The mixer tests distance BEFORE it allocates anything, so a gunshot 200 m away
 * costs one subtraction and one compare — no node, no buffer, no voice. In a
 * city holding ~64 live actors and a traffic fleet this is the difference
 * between an audio graph that idles and one that thrashes. It is why the caps
 * below can be generous.
 *
 * ⚠ **A LOOP IS NOT A ONE-SHOT WITH A FLAG.** Every looping slug holds a real
 * node open for as long as it runs, so its `maxVoices` is a hard ceiling on
 * live sources and not a throttle on starts. The four loop slugs
 * (`car_engine`, `siren_police`, `siren_ambulance`, `drone`) plus `rain` are
 * budgeted together against `SFX_TUNE.maxLoops`.
 */

import { SFX_ASSETS } from './sfxAssets.js';

/**
 * Global mixer limits. Separate from the per-slug table because they are about
 * the MACHINE rather than about any sound.
 */
export const SFX_TUNE = Object.freeze({
  /**
   * Total simultaneous voices. 24 is chosen against the worst real moment this
   * game can produce — a five-way firefight beside a burning car in traffic —
   * measured as ~9 one-shots plus ~5 loops at peak, so it is roughly a 2×
   * margin rather than a number that will be hit every second. It is a POOL,
   * not a budget: exceeding it steals the cheapest voice, it never drops a
   * sound silently at the top of the priority list.
   */
  maxVoices: 24,
  /**
   * Of those, at most this many may be LOOPS. A loop holds its node for
   * minutes, so without a separate ceiling a street of idling cars would take
   * the whole pool and the gunfire would have nothing left to steal.
   */
  maxLoops: 8,
  /**
   * ⚠ THE LISTENER IS THE PLAYER, NOT `interiors.focus`. A shop is a real room
   * 100 m under the island; anchoring the listener on the door ring would pipe
   * the whole street into it. This mirrors `docs/dialogue.md`'s rule for the
   * speech distance test, and for exactly the same reason.
   *
   * Metres of listener travel that count as "teleported" — a swap into an
   * interior, a respawn. Everything positional is re-evaluated and loops are
   * re-faded rather than gliding across a kilometre.
   */
  teleportDist: 40,
  /** Seconds a loop takes to fade in or out. Never zero — a hard stop clicks. */
  loopFade: 0.25,
  /**
   * Fraction of `maxDist` over which a sound fades to nothing before the cull.
   * Without it a loop crossing the boundary pops out mid-cycle.
   */
  cullFade: 0.2,
  /**
   * Stereo width. 1.0 is a hard left/right pan at 90° off the camera axis,
   * which is exhausting on headphones over a long session and wrong for a
   * camera 26 m up looking down at 52°: almost nothing is really beside you.
   */
  panWidth: 0.7,
});

/**
 * @typedef {object} SfxDef
 * @property {'sfx'|'ambient'|'ui'} bus
 * @property {number} gain
 * @property {'random'|'sticky'} [pick]
 * @property {number} priority
 * @property {number} [maxVoices]
 * @property {number} [minGap]
 * @property {number} [refDist]
 * @property {number} [maxDist]
 * @property {number} [rateVar]
 * @property {number} [rateKeyed]
 * @property {string} [from]
 */

/** @type {Record<string, SfxDef>} */
export const SFX_MIX = {
  // ── combat ────────────────────────────────────────────────────────────────

  /**
   * ⚠⚠ **`maxDist` 80 IS A PLAYTEST RESULT AND IT OVERTURNED A CLAIMED
   * DERIVATION.** It shipped at **110** under a note saying that number was
   * DERIVED FROM `FIRE.range` 14. It never was: the real argument was *"110 m is
   * comfortably past the ~38 m the camera sees and past the spawn ring, so EVERY
   * gunshot in the game is audible and nothing beyond the simulation is"* — and
   * that PREMISE is precisely what the owner rejected, playing it: **"police
   * siren and gun shot sounds dropoff is too low, they can be heard all the way
   * from across the map."** Making every shot the simulation fires audible is
   * how a city ends up sounding like it is shooting at you from everywhere at
   * once, and `FIRE.range` was never what set the number.
   *
   * ⚠ **THE NEW RULE IS THE OPPOSITE ONE: a firefight is audible about a block
   * and a half out, not everywhere the simulation reaches.** 80 is ANCHORED, not
   * derived — the camera's footprint is ~38 m across and `streetSpacing` is
   * 54 m, so 80 m is the visible radius plus roughly one more screen-width, i.e.
   * ~1.5 blocks. ⚠⚠ **It is an admitted playtest number WITH an anchor, and
   * saying so is the point**: `docs/stamina.md`'s `STAMINA.mul` note is this
   * project's own record that a FALSE derivation is worse than an honest
   * playtest constant, and the 110 above is what that failure looks like.
   *
   * `refDist` moves 12 → **10** with it, so the near field tightens WITH the
   * reach rather than the far half of one curve simply being lopped off. Worked
   * through `attenuate` at `gain` 0.85: **0.850 at 10 m · 0.425 at 20 · 0.224 at
   * 38 (the edge of the frame) · 0.157 at 54 (one block) · 0.133 at 64, where
   * the `cullFade` ramp starts · 0.076 at 70 · silent at 80.** It was 0.268 at
   * 38 and still an audible 0.046 at 100 m, from a shooter two blocks past
   * anything on screen.
   *
   * ⚠ **AN ADDED ROLLOFF EXPONENT IS A CLOSED ALTERNATIVE for this round.** The
   * law lives in `engine/audio/mixer.js#attenuate`, `tests/audio.test.mjs` (e)
   * pins it continuous at BOTH ends, and EVERY other slug rides it — changing a
   * documented shared law to fix two rows is the wrong blast radius, and this
   * file's own header says re-tune HERE and never in the engine.
   *
   * ⚠ **`car_crash` 130 AND `explosion` 160 ARE DELIBERATELY LEFT ALONE.** The
   * owner named the siren and the gunshot; a detonation carrying further than a
   * pistol is right, and tidiness is not a reason to quieten a bang nobody
   * complained about. Do not "consistency-fix" them down to match.
   *
   * ⚠ `maxVoices` 5 against a possible eight-hostile firefight is DELIBERATE
   * THINNING. Eight pistol cracks inside one 0.28 s interval is not eight
   * gunshots, it is a burst of white noise — and the sixth through eighth are
   * masked by the first five anyway. The cap costs nothing audible and bounds
   * the worst case.
   */
  gunshot: { bus: 'sfx', gain: 0.85, pick: 'random', priority: 8, maxVoices: 5, minGap: 0.03, refDist: 10, maxDist: 80, rateVar: 0.06 },

  /**
   * ⚠ `minGap` 0.25 is the loudest number on this page. Groans are three cuts
   * of ONE take by one actor, so two firing inside a few frames is audibly the
   * same voice twice — which reads as a bug, not as two people. A quarter of a
   * second is enough that the ear takes them as separate events.
   */
  groan: { bus: 'sfx', gain: 0.55, pick: 'random', priority: 4, maxVoices: 3, minGap: 0.25, refDist: 6, maxDist: 45, rateVar: 0.1 },

  // ── the player on foot ────────────────────────────────────────────────────

  /**
   * ⚠ THE PLAYER'S FOOTSTEPS ONLY, AND THAT IS A DESIGN DECISION RATHER THAN A
   * BUDGET ONE. `Population` keeps a pavement full of pedestrians; per-NPC
   * footsteps would be the heaviest channel in the game AND would arrive as
   * mush, because a crowd's steps have no phase relationship. One walker you
   * can hear is a character; twenty you can hear is carpet.
   *
   * Hence `maxDist` 18 — a bound on an accident, since nothing but the player
   * emits these and the player is at distance 0.
   */
  footstep: { bus: 'sfx', gain: 0.3, pick: 'random', priority: 1, maxVoices: 2, minGap: 0.09, refDist: 3, maxDist: 18, rateVar: 0.12 },

  // ── vehicles ──────────────────────────────────────────────────────────────

  car_crash: { bus: 'sfx', gain: 0.9, pick: 'random', priority: 9, maxVoices: 3, minGap: 0.08, refDist: 14, maxDist: 130, rateVar: 0.08 },

  /**
   * ⚠ ALIAS, AND A DOCUMENTED STOPGAP. **The pack ships no explosion.** A car
   * detonating in silence while a smashed bench is audible is worse than an
   * imperfect bang, so this is `car_crash_1` — the 0.96 s heavy impact, the
   * biggest transient in the pack — played at 0.62× rate, which drops it about
   * seven semitones and turns a metal crunch into a low thud. It is defensible
   * and it is not right; a real explosion asset replaces this row and nothing
   * else. ⚠ `variant` PINS the clip: the second `car_crash` is a 2.57 s tumble
   * and pitching it down yields ten seconds of scraping.
   */
  explosion: { from: 'car_crash', variant: 0, bus: 'sfx', gain: 1, priority: 10, maxVoices: 2, minGap: 0.05, refDist: 18, maxDist: 160, rate: 0.62, rateVar: 0.04 },

  car_start: { bus: 'sfx', gain: 0.6, pick: 'sticky', priority: 5, maxVoices: 2, minGap: 0.1, refDist: 8, maxDist: 45 },

  /**
   * ⚠ ONE ENGINE CLIP SHIPPED, AND `rateKeyed` IS WHAT MAKES THAT SURVIVABLE.
   * The pack lost its second engine loop in the cull, so every car in the city
   * would idle on the same 8 s buffer at the same pitch — which is far more
   * obviously wrong than a shared sample, because identical PITCH is what the
   * ear detects. `rateKeyed` 0.18 hands each car archetype a rate offset hashed
   * from its own key, so a saloon and a van hold audibly different notes and
   * each one holds ITS note for ever. Same trick, one buffer, no extra bytes.
   *
   * ⚠⚠ **`gain` 0.13 IS A PLAYTEST RESULT, NOT A GUESS — IT SHIPPED AT 0.35 AND
   * THE OWNER'S REPORT WAS "the only thing I hear".** The arithmetic that made
   * 0.35 look reasonable was per-voice and the mistake was per-SCENE: this is
   * the one slug that is CONTINUOUS, and `maxVoices` 4 means up to four of them
   * sum at once, so a quiet street was carrying ~1.4 of engine against a 0.85
   * gunshot that fires for half a second. It is now 0.13 — a **63% cut**, past
   * the 50% asked for, chosen so that four summed engines (~0.52) still sit
   * under a single gunshot.
   *
   * ⚠ The lever is `gain` and NOT `maxVoices` or `refDist`. Dropping to two
   * engines would empty the street, and tightening `refDist` would make the
   * player's OWN car — the one at distance ~0 that should be loudest — no
   * quieter at all, which is the opposite of the report.
   */
  car_engine: { bus: 'sfx', gain: 0.13, pick: 'sticky', priority: 3, maxVoices: 4, refDist: 7, maxDist: 40, rateKeyed: 0.18 },

  /**
   * ⚠⚠ **`maxDist` 120 IS A PLAYTEST RESULT AND IT REPLACED 160.** This row used
   * to hold the largest number in the table and to argue that the size WAS the
   * feature: *"a wanted level you can only hear once the police are on top of
   * you is a jump scare; one you hear four blocks out is a chase."* The owner
   * played it: **"police siren and gun shot sounds dropoff is too low, they can
   * be heard all the way from across the map."** Four blocks out is not a chase
   * — at that range there is nothing on screen for the siren to be coming FROM,
   * so it stops being a warning and becomes weather.
   *
   * ⚠ **THE BEAT THAT ACTUALLY MATTERS IS THE ARRIVAL, AND 120 STILL COVERS IT
   * WITH ROOM TO SPARE.** `WANTED.carRingMin`/`carRingMax` join a response car
   * to the network **70–100 m** from the player, so a cruiser dispatched at the
   * far edge of that ring is audible from the instant it exists and gets louder
   * every metre of the way in — which is the whole dramatic content of the
   * siren. Worked through `attenuate` at `gain` 0.5: **0.500 at 16 m · 0.267 at
   * 30 · 0.148 at 54 (one block) · 0.133 at 60 · 0.083 at 96, where the
   * `cullFade` ramp starts · 0.067 at 100 (the far edge of the dispatch ring) ·
   * silent at 120.** It was 0.125 at 100 m and still climbing out to 160, i.e.
   * loudest exactly where no police car had been spawned yet.
   *
   * `refDist` moves 25 → **16** for the reason the gunshot's does: the near
   * field tightens with the reach instead of one curve being truncated.
   *
   * ⚠ **`priority` 7 IS UNCHANGED, AND IT IS THE OTHER HALF OF THE FEATURE.** A
   * siren cut off by a bench being smashed reads as the police having given up.
   * The fix here was REACH; rank was never the problem.
   *
   * ⚠ **WHERE the siren comes from is `GameAudio#siren()`'s question, not this
   * row's, and it moved in the same round** — the loop is sited on the nearest
   * police CAR (the same set whose lightbar is flashing), never on a person and
   * never on the listener. ★1 is two foot officers and no car, so ★1 is SILENT
   * BY DESIGN. See `docs/audio.md`; that fix and this one are the two halves of
   * one report, and widening `maxDist` again is not a substitute for either.
   */
  siren_police: { bus: 'sfx', gain: 0.5, pick: 'sticky', priority: 7, maxVoices: 3, refDist: 16, maxDist: 120 },

  /** The Wasted bed. 2D on purpose — it is scored over a dead player, not sited in the world. */
  siren_ambulance: { bus: 'ambient', gain: 0.45, pick: 'sticky', priority: 9 },

  // ── the world ─────────────────────────────────────────────────────────────

  prop_smash: { bus: 'sfx', gain: 0.7, pick: 'random', priority: 5, maxVoices: 3, minGap: 0.05, refDist: 8, maxDist: 60, rateVar: 0.1 },

  /** Sticky per building, so a shop's door always sounds like that shop's door. */
  door: { bus: 'sfx', gain: 0.5, pick: 'sticky', priority: 4, maxVoices: 2, refDist: 4, maxDist: 25 },

  /**
   * ⚠ THE RAIN BED IS 2D AND MUST NEVER BECOME POSITIONAL. It has no source to
   * be at — it is everywhere — and a panned rain loop swings the whole weather
   * around the player's head every time the camera turns. `priority` 10 keeps
   * it unstealable: a bed that drops out under gunfire is far more noticeable
   * than a missing gunshot.
   */
  rain: { bus: 'ambient', gain: 0.4, pick: 'sticky', priority: 10 },

  drone: { bus: 'sfx', gain: 0.45, priority: 3, maxVoices: 1, refDist: 8, maxDist: 60 },

  // ── the phone and the UI ──────────────────────────────────────────────────
  //
  // ⚠ EVERY ROW HERE IS ON THE `ui` BUS, WHICH IS THE ONE THAT SURVIVES THE
  // FREEZE. Five overlays can halt the fixed step (`ui/hud/freezeOwner.js`),
  // and a shop panel whose purchase makes no sound because the world is paused
  // is a dead button. UI sounds are 2D, never distance-culled, and never ducked.

  phone_ring: { bus: 'ui', gain: 0.6, pick: 'sticky', priority: 10, maxVoices: 1 },
  phone_key: { bus: 'ui', gain: 0.4, pick: 'random', priority: 6, maxVoices: 3, minGap: 0.02 },
  cash: { bus: 'ui', gain: 0.6, pick: 'random', priority: 8, maxVoices: 2, minGap: 0.1 },
  ui_notify: { bus: 'ui', gain: 0.5, priority: 8, maxVoices: 2, minGap: 0.05 },
  text_notify: { bus: 'ui', gain: 0.5, priority: 8, maxVoices: 2, minGap: 0.05 },
};

/**
 * Default bus levels, 0..1. The player's own settings multiply these and are
 * persisted by the UI; these are the shipped mix.
 *
 * ⚠ `master` STARTS AT 0.7, NOT 1. Headroom: `SFX_MIX` is tuned so that a
 * genuinely busy moment sums well under clipping, and the remaining 30% is
 * what a player who turns it up is turning up INTO. A mix that ships at unity
 * can only ever get quieter.
 */
export const SFX_LEVELS = Object.freeze({ master: 0.7, sfx: 1, ambient: 0.8, ui: 0.9 });

/**
 * Build the injected sound table. Mirrors `createWeaponTable()` /
 * `createFactionRules()` — the route calls this, the engine never does.
 *
 * @param {object} [o]
 * @param {Record<string, Partial<SfxDef>>} [o.overrides] Per-slug tuning patch. The
 *   review scene uses it to A/B a level without editing content; nothing shipped does.
 * @returns {{assets: typeof SFX_ASSETS, mix: Record<string, SfxDef>, tune: typeof SFX_TUNE, levels: typeof SFX_LEVELS}}
 */
export function createSoundTable(o = {}) {
  const { overrides = null } = o ?? {};
  /** @type {Record<string, SfxDef>} */
  const mix = {};
  for (const [slug, def] of Object.entries(SFX_MIX)) {
    // ⚠ An alias resolves HERE, in content, not in the mixer. The mixer should
    // never have to know that two slugs share a buffer — that is a fact about
    // which clips the designer happened to ship, and it changes the day an
    // explosion asset arrives.
    const src = def.from ?? slug;
    if (!SFX_ASSETS[src]) continue; // a slug whose clips were culled simply does not exist
    mix[slug] = { ...def, src, ...(overrides?.[slug] ?? null) };
  }
  return { assets: SFX_ASSETS, mix, tune: SFX_TUNE, levels: SFX_LEVELS };
}
